/**
 * API Client
 * 
 * Centralized HTTP client with:
 * - Auto-include auth token from sessionStorage
 * - Response parsing
 * - 401 redirect to sign-in
 * - Configurable base URL
 */

import { ROUTES } from './constants';
import { readToken } from '../auth/session';

/**
 * Get the base URL for API requests
 * In development, return empty string to use Vite proxy
 * In production, use the configured base URL
 * @returns {string} The base URL
 */
const getBaseUrl = () => {
  // In development, use empty string to leverage Vite proxy
  if (import.meta.env.DEV) {
    return '';
  }
  // In production, use the configured base URL
  return import.meta.env.VITE_API_BASE_URL || '';
};

/**
 * Get the auth token from sessionStorage
 * @returns {string|null} The auth token or null
 */
const getAuthToken = () => {
  return readToken();
};

/**
 * Build headers for API requests
 * @param {Object} customHeaders - Additional headers to include
 * @returns {Object} Headers object
 */
const buildHeaders = (customHeaders = {}) => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json;charset=utf-8',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...customHeaders,
  };
};

/**
 * Handle API response with comprehensive error handling
 * @param {Response} response - Fetch response object
 * @returns {Promise} Parsed response data
 */
const handleResponse = async (response) => {
  // Handle 401 Unauthorized - redirect to sign-in (but not from landing page)
  if (response.status === 401 && window.location.pathname !== ROUTES.SIGN_IN && window.location.pathname !== '/') {
    console.warn('🔒 Unauthorized access - redirecting to sign-in');
    window.location.href = ROUTES.SIGN_IN;
    return Promise.reject(new Error('Unauthorized - Please sign in again'));
  }

  // Handle 403 Forbidden - access denied
  if (response.status === 403) {
    const error = new Error('Access denied - You do not have permission to perform this action');
    error.status = 403;
    error.isAccessDenied = true;
    throw error;
  }

  // Handle 404 Not Found
  if (response.status === 404) {
    const error = new Error('Resource not found');
    error.status = 404;
    error.isNotFound = true;
    throw error;
  }

  // Handle 500 Server Error
  if (response.status >= 500) {
    const error = new Error('Server error - Please try again later or contact support');
    error.status = response.status;
    error.isServerError = true;
    throw error;
  }

  // 204 No Content (and 205) carry no body by definition, and some endpoints
  // return an empty 200. Calling response.json() on those throws a parse error,
  // which the handling below then reports as a server failure. The docs
  // analytics endpoint answers 204 for any request that has never been
  // executed, so every freshly imported API showed "Server error" on its
  // documentation page.
  const contentType = response.headers.get('content-type') || '';

  let data;
  if (response.status === 204 || response.status === 205) {
    data = null;
  } else if (contentType.includes('application/json')) {
    const raw = await response.text();
    data = raw ? JSON.parse(raw) : null;
  } else if (contentType.includes('octet-stream')) {
    data = await response.blob();
  } else {
    data = await response.text();
  }

  if (!response.ok) {
    const error = new Error(data?.message || `HTTP error! status: ${response.status}`);
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
};

/**
 * Check if an error is retryable (network failure, timeout, or 5xx error)
 * @param {Error} error - The error to check
 * @returns {boolean} True if the error is retryable
 */
const isRetryableError = (error) => {
  // Network failures (no response)
  if (!error.status) {
    return true;
  }
  
  // Server errors (5xx)
  if (error.status >= 500) {
    return true;
  }
  
  // Timeout errors
  if (error.name === 'AbortError' || error.message?.includes('timeout')) {
    return true;
  }
  
  return false;
};

/**
 * Sleep for a specified duration
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise<void>}
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Build URL with query parameters
 * @param {string} url - Base URL
 * @param {Object} params - Query parameters object
 * @returns {string} URL with query string
 */
const buildUrlWithParams = (url, params) => {
  if (!params || Object.keys(params).length === 0) {
    return url;
  }
  
  const queryString = Object.entries(params)
    .filter(([_, value]) => value !== null && value !== undefined)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
  
  return queryString ? `${url}?${queryString}` : url;
};

/**
 * Make an API request with retry logic (internal helper)
 * @param {string} url - The URL to request
 * @param {Object} options - Fetch options
 * @param {number} [retries=0] - Number of retries remaining
 * @returns {Promise} Response data
 */
const makeRequest = async (url, options = {}, retries = 0) => {
  const baseUrl = getBaseUrl();
  
  // Extract params from options and build URL with query string
  const { params, ...fetchOptions } = options;
  const urlWithParams = buildUrlWithParams(url, params);
  const fullUrl = urlWithParams.startsWith('http') ? urlWithParams : `${baseUrl}${urlWithParams}`;

  const config = {
    ...fetchOptions,
    headers: buildHeaders(fetchOptions.headers),
    credentials: 'include',
  };

  try {
    const response = await fetch(fullUrl, config);
    return await handleResponse(response);
  } catch (error) {
    console.error('API request failed:', error);
    
    // Check if we should retry
    if (retries > 0 && isRetryableError(error)) {
      const delay = Math.min(1000 * Math.pow(2, 3 - retries), 4000); // Exponential backoff: 1s, 2s, 4s
      await sleep(delay);
      return makeRequest(url, options, retries - 1);
    }
    
    // Enhance error with retry information
    error.isNetworkError = !error.status;
    error.retriesExhausted = retries === 0 && isRetryableError(error);
    
    throw error;
  }
};

/**
 * Legacy request function for backward compatibility
 * Matches the signature of the old network.js request function
 * @param {string} url - The URL to request
 * @param {string} method - HTTP method (GET, POST, PUT, DELETE)
 * @param {Object} body - Request body data
 * @param {string} contentType - Content type (default: application/json)
 * @param {Object} headers - Additional headers
 * @returns {Promise} Response data
 */
const request = async (url, method = 'GET', body = null, contentType = 'application/json', headers = {}) => {
  const token = getAuthToken();
  const requestHeaders = {
    ...headers,
    Authorization: token ? `Bearer ${token}` : '',
    'Content-Type': contentType + ';charset=utf-8',
  };

  const baseUrl = getBaseUrl();
  const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;

  try {
    const response = await fetch(fullUrl, {
      method,
      body: body != null ? JSON.stringify(body) : null,
      headers: requestHeaders,
      credentials: 'include',
    });

    // Check if the response status is Unauthorized (401) - but not from landing page
    if (response.status === 401 && window.location.pathname !== ROUTES.SIGN_IN && window.location.pathname !== '/') {
      window.location.href = ROUTES.SIGN_IN;
      return Promise.reject('Unauthorized');
    }

    // Same empty-body guard as above.
    const responseContentType = response.headers.get('content-type') || '';
    let data = null;
    if (response.status === 204 || response.status === 205) {
      data = null;
    } else if (responseContentType.includes('application/json')) {
      const raw = await response.text();
      data = raw ? JSON.parse(raw) : null;
    } else if (responseContentType.includes('octet-stream')) {
      data = await response.blob();
    } else {
      data = await response.text();
    }
    return data; // Return only the data
  } catch (err) {
    console.error(err);
    throw err;
  }
};

/**
 * API client with HTTP method helpers
 */
const api = {
  /**
   * Make a GET request
   * @param {string} url - The URL to request
   * @param {Object} config - Additional fetch config
   * @param {number} [config.retries=0] - Number of retry attempts for network failures
   * @returns {Promise} Response data
   */
  get: (url, config = {}) => {
    const { retries = 0, ...fetchConfig } = config;
    return makeRequest(url, {
      ...fetchConfig,
      method: 'GET',
    }, retries);
  },

  /**
   * Make a POST request
   * @param {string} url - The URL to request
   * @param {Object} data - Request body data
   * @param {Object} config - Additional fetch config
   * @param {number} [config.retries=0] - Number of retry attempts for network failures
   * @returns {Promise} Response data
   */
  post: (url, data, config = {}) => {
    const { retries = 0, ...fetchConfig } = config;
    return makeRequest(url, {
      ...fetchConfig,
      method: 'POST',
      body: data ? JSON.stringify(data) : null,
    }, retries);
  },

  /**
   * Make a PUT request
   * @param {string} url - The URL to request
   * @param {Object} data - Request body data
   * @param {Object} config - Additional fetch config
   * @param {number} [config.retries=0] - Number of retry attempts for network failures
   * @returns {Promise} Response data
   */
  put: (url, data, config = {}) => {
    const { retries = 0, ...fetchConfig } = config;
    return makeRequest(url, {
      ...fetchConfig,
      method: 'PUT',
      body: data ? JSON.stringify(data) : null,
    }, retries);
  },

  /**
   * Make a DELETE request
   * @param {string} url - The URL to request
   * @param {Object} config - Additional fetch config
   * @param {number} [config.retries=0] - Number of retry attempts for network failures
   * @returns {Promise} Response data
   */
  delete: (url, config = {}) => {
    const { retries = 0, ...fetchConfig } = config;
    return makeRequest(url, {
      ...fetchConfig,
      method: 'DELETE',
    }, retries);
  },

  /**
   * Make a PATCH request
   * @param {string} url - The URL to request
   * @param {Object} data - Request body data
   * @param {Object} config - Additional fetch config
   * @param {number} [config.retries=0] - Number of retry attempts for network failures
   * @returns {Promise} Response data
   */
  patch: (url, data, config = {}) => {
    const { retries = 0, ...fetchConfig } = config;
    return makeRequest(url, {
      ...fetchConfig,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : null,
    }, retries);
  },
};

export default api;
export { getAuthToken, getBaseUrl, request };
